import { useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';

import type { SeniorityLevelDto } from '@/api/catalog-client';
import { CatalogSupplierClientProvider } from '@/api/catalog-client-context';
import { SENIORITY_LEVELS } from '@/lib/catalog-labels';
import type { CatalogReferentialSearch } from '@/routes/supplier/catalog/catalog-referential-model';
import { CatalogReferentialScreen } from '@/routes/supplier/catalog/catalog-referential-screen';

// =============================================================================
// catalog-referential-route.tsx — Adaptateur routeur de la consultation Uc-CAT-PROFILS (DBU §3.4).
// =============================================================================
// Route `/catalog` (monde FOURNISSEUR, P-05 : URL distincte de l'admin, aucun bouton d'écriture).
// Le BC catalog AUTO-PORTE son DI seam : la route enveloppe l'écran avec le CatalogSupplierClientProvider
// local (défaut = stub / impl HTTP) plutôt que de brancher le client dans providers.tsx.
//
// L'ÉTAT DE VUE (famille / sousFamille / niveau / q / profil) est porté par l'URL (search params
// TanStack ; RULE_REF_006 : jamais localStorage). L'adaptateur lit l'URL (`useSearch`) et la met à
// jour (`useNavigate`) ; l'écran reste CONTRÔLÉ et testable hors routeur.

/** Réexport du type d'état de vue (source unique : le model). */
export type { CatalogReferentialSearch } from '@/routes/supplier/catalog/catalog-referential-model';

/** Un niveau valide (parité SENIORITY_LEVELS). */
function isSeniorityLevel(value: unknown): value is SeniorityLevelDto {
  return (
    typeof value === 'string' &&
    (SENIORITY_LEVELS as readonly string[]).includes(value)
  );
}

/**
 * Valide/normalise les 5 search params du référentiel (état d'URL de vue). Une valeur absente ou
 * invalide n'est PAS sérialisée (URL propre). Aucune règle métier : simple garde de type.
 */
export function validateCatalogReferentialSearch(
  search: Record<string, unknown>,
): CatalogReferentialSearch {
  const result: {
    famille?: string;
    sousFamille?: string;
    niveau?: SeniorityLevelDto;
    q?: string;
    profil?: string;
  } = {};
  const famille = search['famille'];
  if (typeof famille === 'string' && famille.length > 0) {
    result.famille = famille;
  }
  const sousFamille = search['sousFamille'];
  if (typeof sousFamille === 'string' && sousFamille.length > 0) {
    result.sousFamille = sousFamille;
  }
  const niveau = search['niveau'];
  if (isSeniorityLevel(niveau)) {
    result.niveau = niveau;
  }
  const q = search['q'];
  if (typeof q === 'string' && q.length > 0) {
    result.q = q;
  }
  const profil = search['profil'];
  if (typeof profil === 'string' && profil.length > 0) {
    result.profil = profil;
  }
  return result;
}

export function CatalogReferentialRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as CatalogReferentialSearch;

  const onChange = React.useCallback(
    (next: CatalogReferentialSearch): void => {
      void navigate({ to: '/catalog', search: next });
    },
    [navigate],
  );

  // R15-CMP-03 — deep-link du CTA « Saisir mon TJM » : navigation cross-route vers la GRILLE de saisie
  // (`/saisie/$submissionId`) avec le profil ancré en search param (`profil`) → la cellule est focalisée à
  // l'ouverture (saisie-screen). La cible (submissionId) est résolue côté API (F0-CONTRACT-4, RLS n°1).
  const onSaisirTjm = React.useCallback(
    (target: { readonly submissionId: string }, profileCode: string): void => {
      void navigate({
        to: '/saisie/$submissionId',
        params: { submissionId: target.submissionId },
        search: { profil: profileCode },
      });
    },
    [navigate],
  );

  return (
    <CatalogSupplierClientProvider>
      <CatalogReferentialScreen
        search={search}
        onChange={onChange}
        onSaisirTjm={onSaisirTjm}
      />
    </CatalogSupplierClientProvider>
  );
}
